import '.aw{flex-shrink:0;}.ssr-plugin'
import '.bo{height:100%;}.ssr-plugin'
import '.p{padding:1rem;}.ssr-plugin'
import '.j{display:flex;}.ssr-plugin'
import '.v{flex-direction:column;}.ssr-plugin'
import '.ae{align-items:stretch;}.ssr-plugin'
import '.b1{gap:0.5rem;}.ssr-plugin'
import '.bi{min-width:0px;}.ssr-plugin'
import '.c2{flex:1;}.ssr-plugin'
import '.c5{align-self:flex-end;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import {
  Children,
  PropsWithChildren,
  ReactNode,
  isValidElement,
  useMemo,
} from 'react'
import { Image } from '@app/components/core/image/Image'

export interface CardProps extends PropsWithChildren {
  image?: string | ReactNode
  title?: string
  subtitle?: string
  orientation?: 'vertical' | 'horizontal'
  padded?: boolean
  contentStyle?: Style
  border?: boolean
  style?: Style
  imageStyle?: Style
  onClick?: () => void
  'data-testid'?: string
}

const CardActions = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

const CardMeta = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

const onClickStyles: Style = {
  appearance: 'none',
  fontFamily: 'inherit',
  fontSize: 'inherit',
}

const CardComponent = ({
  image,
  title,
  subtitle,
  orientation = 'vertical',
  padded,
  border = true,
  style,
  imageStyle,
  contentStyle,
  onClick,
  children,
  'data-testid': testId = 'card',
}: CardProps) => {
  const { actions, meta, content } = useMemo(() => {
    const childrenArray = Children.toArray(children)
    let actions
    let meta
    const content: ReactNode[] = []

    for (const child of childrenArray) {
      if (isValidElement(child)) {
        if (child.type === CardActions) {
          if (!actions) {
            actions = child
          }
          continue
        }
        if (child.type === CardMeta) {
          if (!meta) {
            meta = child
          }
          continue
        }
      }
      content.push(child)
    }

    return { actions, meta, content }
  }, [children])

  const RootElement = onClick ? styled.button : styled.div

  return (
    <RootElement
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: 0,
        position: 'relative',
        backgroundColor: 'var(--shades-white)',
        borderRadius: border ? '20px' : '0',
        border: border ? 'var(--gray-border)' : 'none',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        width: '100%',
        display: 'flex',
        flexDirection: orientation === 'horizontal' ? 'row' : 'column',
        gap: orientation === 'horizontal' ? '1rem' : undefined,
        alignItems: orientation === 'horizontal' ? 'stretch' : undefined,
        transition: 'transform 0.2s ease, opacity 0.2s ease',
        textAlign: 'left',
        ...(onClick ? onClickStyles : {}),
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              opacity: 0.9,
            }
          : {},
        ...style,
      }}
    >
      {/* Image Section */}
      {image && (
        <styled.div className="aw"
          style={{
            padding: padded
              ? orientation === 'horizontal'
                ? '1rem 0 1rem 1rem'
                : '1rem 1rem 0 1rem'
              : 0,
            width: orientation === 'horizontal' ? '140px' : '100%',
            minWidth: orientation === 'horizontal' ? '140px' : undefined,
            height: orientation === 'horizontal' ? undefined : '192px',
            ...imageStyle,
          }}
        >
          {isValidElement(image) ? (
            image
          ) : (
            <Image
              src={image as string}
              alt={title || ''}
              style={{
                borderRadius: padded ? '10px' : 0,
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          )}
        </styled.div>
      )}

      {/* Content Section */}
      <styled.div className="bo p j v ae b1 bi"
        style={{
          flex: orientation === 'horizontal' ? 1 : undefined,
          position: orientation === 'horizontal' ? undefined : 'relative',
          ...contentStyle,
        }}
      >
        {/* Category Tag */}
        {meta && meta.props.children}

        {/* Title */}
        {title && (
          <div>
            <Text.Large
              style={{
                color: 'var(--primary-blue)',
                margin: 0,
                fontWeight: '500',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {title}
            </Text.Large>
            {subtitle && (
              <Text.Small
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--shades-blue-500)',
                  fontWeight: '500',
                  width: '100%',
                  lineHeight: '100%',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {subtitle}
              </Text.Small>
            )}
          </div>
        )}

        {content}

        {/* Spacer to push actions down */}
        {actions && <div className="c2" />}

        {/* Actions */}
        {actions && (
          <div className="j c5 b1"
          >
            {actions.props.children}
          </div>
        )}
      </styled.div>
    </RootElement>
  )
}

export const Card = Object.assign(CardComponent, {
  Actions: CardActions,
  Meta: CardMeta,
})
